import React from 'react';
import { Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { MeterRow } from '@custom-types/dollarJournalDashboard';
import type { TestAttrs } from '@utils/testAttr';

const fmtCurrency = (v: number | string | null | undefined) => {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Data columns for the Dollar Journal table.
 * Maps display labels → DB fields → render logic.
 * Interactive columns (Approval Status, Approve) are defined inline in DollarJournalDashboardPage.tsx.
 *
 * Field map (label → dataIndex → source):
 *   Vendor              → vendor_name            → IppMaster.vendor_name
 *   Name                → location               → MeterDataComputed.location
 *   Meter Number        → meter_number           → MeterMaster.meter_number
 *   Meter Read          → post_prod_stat_meter_read_type → MeterDataComputed
 *   Invoice Net Gen     → post_prod_stat_netgen  → calcPostProdNetgen (computed/stored)
 *   AGC Energy          → agc_energy_kwh         → AgcEnergySummary.agc_energy_kwh
 *   Rate                → rate                   → RateContract.rate
 *   Gen Cost            → prod_stat_cost_original → post_prod_stat_netgen * rate
 *   AGC Cost            → agc_cost               → agc_energy_kwh * rate
 *   SPP Adj             → adjustments            → retro + overage + shortfall + negGen + adHoc
 *   SPP Cost            → dollar_value           → prod_stat_cost_adjusted + agc_cost
 *   Invoice Adj         → invoice_adjustments    → admin_fee + forecast_fee + counter_party_fee
 */
export function buildDataColumns(
  styles: Record<string, string>,
  hasAgc = false,
): ColumnsType<MeterRow> {
  const AGC_KEYS = new Set(['agc_energy_kwh', 'agc_cost']);
  return ([
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 80,
      ellipsis: true,
      fixed: 'left',
      sorter: (a, b) => (a.vendor_name ?? '').localeCompare(b.vendor_name ?? ''),
      sortDirections: ['ascend', 'descend', null],
      onHeaderCell: () => ({ className: styles.vendorHeader }),
      onCell: () => ({ className: styles.vendorCell }),
    },
    {
      title: 'Name',
      dataIndex: 'location',
      key: 'location',
      width: 80,
      ellipsis: true,
      fixed: 'left',
      sorter: (a, b) => (a.location ?? '').localeCompare(b.location ?? ''),
      sortDirections: ['ascend', 'descend', null],
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Meter Number',
      dataIndex: 'meter_number',
      key: 'meter_number',
      width: 130,
      fixed: 'left',
      sorter: (a, b) =>
        (a.meter_number ?? '').toString().localeCompare((b.meter_number ?? '').toString()),
      sortDirections: ['ascend', 'descend', null],
      render: (_: any, record: MeterRow) => (
        <Link to={`/meterreaddetails/${record.meter_id}`} data-testid="meter-number-link">
          {record.meter_number}
        </Link>
      ),
    },
    {
      title: 'Meter Read',
      dataIndex: 'post_prod_stat_meter_read_type',
      key: 'post_prod_stat_meter_read_type',
      width: 110,
      ellipsis: true,
      sorter: (a, b) =>
        (a.post_prod_stat_meter_read_type ?? '').localeCompare(b.post_prod_stat_meter_read_type ?? ''),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Invoice Net Gen',
      dataIndex: 'post_prod_stat_netgen',
      key: 'post_prod_stat_netgen',
      width: 115,
      align: 'left',
      onHeaderCell: () => ({ title: 'kWh' }),
      render: (v: number | null, record: MeterRow) => (
        <span
          style={{
            color: record.needs_netgen_validation ? '#ff4d4f' : 'inherit',
            fontWeight: record.needs_netgen_validation ? 'bold' : 'normal',
          }}
        >
          {v == null ? '0' : Math.round(v).toLocaleString(undefined)}
        </span>
      ),
      sorter: (a, b) => (a.post_prod_stat_netgen ?? 0) - (b.post_prod_stat_netgen ?? 0),
      sortDirections: ['ascend', 'descend', null],
      onCell: (record: MeterRow) => ({
        style: {
          backgroundColor: record.needs_netgen_validation ? '#fff1f0' : 'inherit',
        },
        'data-highlighted': String(Boolean(record.needs_netgen_validation)),
      }) as TestAttrs,
    },
    {
      title: 'AGC Energy',
      dataIndex: 'agc_energy_kwh',
      key: 'agc_energy_kwh',
      width: 145,
      align: 'left',
      render: (v: number | null) =>
        v == null ? '—' : Math.round(v).toLocaleString(undefined),
      sorter: (a, b) => (a.agc_energy_kwh ?? 0) - (b.agc_energy_kwh ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 130,
      ellipsis: true,
      render: (v: number | null, record: MeterRow) => {
        const formatted = fmtCurrency(v);
        if (
          record.rate_type &&
          record.rate_type.toLowerCase() !== 'contract'
        ) {
          const tooltip = `${record.rate_type}: ${record.rate_effective_from ?? '—'} to ${record.rate_effective_to ?? '—'}`;
          return (
            <Tooltip title={tooltip}>
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }} data-tooltip={tooltip}>
                {formatted}
              </span>
            </Tooltip>
          );
        }
        return formatted;
      },
      sorter: (a, b) => (a.rate ?? 0) - (b.rate ?? 0),
      sortDirections: ['ascend', 'descend', null],
      onCell: (record: MeterRow) => ({
        style: {
          backgroundColor:
            record.rate_type &&
              record.rate_type.toLowerCase() !== 'contract'
              ? '#fff1f0'
              : 'inherit',
        },
        'data-highlighted': String(Boolean(record.rate_type && record.rate_type.toLowerCase() !== 'contract')),
      }) as TestAttrs,
    },
    {
      title: 'Gen Cost',
      dataIndex: 'prod_stat_cost_original',
      key: 'prod_stat_cost_original',
      width: 175,
      ellipsis: true,
      render: fmtCurrency,
      sorter: (a, b) => (a.prod_stat_cost_original ?? 0) - (b.prod_stat_cost_original ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'AGC Cost',
      dataIndex: 'agc_cost',
      key: 'agc_cost',
      width: 120,
      ellipsis: true,
      render: (v: number | null, record: MeterRow) => {
        const type = record.meter_type?.toLowerCase();
        if (type !== 'agc wind farm' && type !== 'max cap wind farm') return '—';
        return fmtCurrency(v);
      },
      sorter: (a, b) => (a.agc_cost ?? 0) - (b.agc_cost ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'SPP Adj',
      dataIndex: 'adjustments',
      key: 'adjustments',
      width: 120,
      ellipsis: true,
      render: fmtCurrency,
      sorter: (a, b) => (a.adjustments ?? 0) - (b.adjustments ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'SPP Cost',
      dataIndex: 'dollar_value',
      key: 'dollar_value',
      width: 130,
      ellipsis: true,
      render: fmtCurrency,
      sorter: (a, b) => (a.dollar_value ?? 0) - (b.dollar_value ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
    {
      title: 'Invoice Adj',
      dataIndex: 'invoice_adjustments',
      key: 'invoice_adjustments',
      width: 120,
      ellipsis: true,
      render: fmtCurrency,
      sorter: (a, b) => (a.invoice_adjustments ?? 0) - (b.invoice_adjustments ?? 0),
      sortDirections: ['ascend', 'descend', null],
    },
  ] as ColumnsType<MeterRow>).filter((col) => hasAgc || !AGC_KEYS.has((col as any).key));
}
